import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
export const SessionMediaTypeSchema = z.enum(['audio', 'video', 'metadata', 'other']);
export const SessionMediaAssetRoleSchema = z.enum([
    'primary_recording',
    'browser_recording',
    'compressed_recording',
    'waveform_peaks',
    'alternate_track',
    'screen_share',
    'provider_metadata',
    'thumbnail',
    'other',
]);
export const RecordingDerivativeKindSchema = z.enum(['compression', 'waveform']);
export const RecordingDerivativeStatusSchema = z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
]);
export const PlaybackVariantSchema = z.enum(['clean-c-v1', 'obfuscated-c-v1']);
export const WaveformPeaksSchema = z.object({
    version: z.literal(1),
    durationSeconds: z.number().positive(),
    sampleRateHz: z.number().multipleOf(1).positive(),
    channelCount: z.number().multipleOf(1).positive(),
    channelRoles: z.array(z.enum(['participant', 'interviewer'])).optional(),
    maxLength: z.number().multipleOf(1).positive(),
    precision: z.number().multipleOf(1).positive(),
    peaks: z.array(z.array(z.number())),
    generatedFromMediaAssetId: UuidSchema,
});
export const SessionMediaAssetSchema = z.object({
    id: UuidSchema,
    sessionId: UuidSchema,
    projectId: UuidSchema,
    organizationId: UuidSchema,
    mediaType: SessionMediaTypeSchema,
    assetRole: SessionMediaAssetRoleSchema,
    isPrimary: z.boolean(),
    blobPath: z.string().min(1),
    contentType: z.string().min(1),
    sizeBytes: z.number().multipleOf(1).nonnegative().nullable(),
    etag: z.string().nullable(),
    sourceProvider: z.string().min(1),
    sourceAssetId: z.string().nullable(),
    durationSeconds: z.number().multipleOf(1).nonnegative().nullable(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    capturedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
});
export const SessionMediaAssetListResponseSchema = z.object({
    items: z.array(SessionMediaAssetSchema),
});
export const CreateMediaUploadUrlRequestSchema = z.object({
    mediaType: SessionMediaTypeSchema,
    assetRole: SessionMediaAssetRoleSchema.prefault('primary_recording'),
    sourceProvider: z.string().min(1),
    sourceAssetId: z.string().min(1).optional(),
    contentType: z.string().min(1).prefault('application/octet-stream'),
    fileName: z.string().min(1).optional(),
    expiresInSeconds: z.number().multipleOf(1).positive().max(3600).optional(),
});
export const CreateMediaUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    blobPath: z.string().min(1),
    expiresInSeconds: z.number().multipleOf(1).positive(),
});
export const CompleteMediaUploadRequestSchema = z.object({
    mediaType: SessionMediaTypeSchema,
    assetRole: SessionMediaAssetRoleSchema.prefault('primary_recording'),
    isPrimary: z.boolean().prefault(false),
    blobPath: z.string().min(1),
    contentType: z.string().min(1),
    sizeBytes: z.number().multipleOf(1).nonnegative().optional(),
    etag: z.string().optional(),
    sourceProvider: z.string().min(1),
    sourceAssetId: z.string().min(1).optional(),
    durationSeconds: z.number().multipleOf(1).nonnegative().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    capturedAt: IsoDateTimeSchema.optional(),
});
export const RecordingAccessResponseSchema = z.object({
    url: z.string().url(),
    expiresInSeconds: z.number().multipleOf(1).positive(),
    media: SessionMediaAssetSchema,
    selectedAssetRole: SessionMediaAssetRoleSchema.optional(),
});
export const MediaAssetAccessResponseSchema = RecordingAccessResponseSchema;
export const WaveformPeaksResponseSchema = z.object({
    media: SessionMediaAssetSchema,
    waveformPeaks: WaveformPeaksSchema,
});
export const PendingIngestionSessionSchema = z.object({
    id: UuidSchema,
    projectId: UuidSchema,
    organizationId: UuidSchema,
    callId: z.string().nullable(),
    conversationProvider: z.string().nullable(),
    providerIngestionStatus: z.string(),
    providerIngestionAttempts: z.number().multipleOf(1).nonnegative(),
    providerIngestionError: z.string().nullable().optional(),
    recordingUrl: z.string().nullable(),
    endedAt: IsoDateTimeSchema.nullable(),
});
export const PendingIngestionSessionsResponseSchema = z.object({
    items: z.array(PendingIngestionSessionSchema),
});
export const MarkProviderIngestionRequestSchema = z.object({
    status: z.enum(['pending', 'processing', 'completed', 'failed', 'unavailable']),
    error: z.string().nullable().optional(),
});
export const MarkRecordingDerivativeRequestSchema = z.object({
    status: RecordingDerivativeStatusSchema,
    error: z.string().nullable().optional(),
});
export const PendingRecordingDerivativeSessionSchema = z.object({
    id: UuidSchema,
    projectId: UuidSchema,
    organizationId: UuidSchema,
    primaryMediaAssetId: UuidSchema,
    primaryMediaContentType: z.string().min(1),
    primaryMediaSourceProvider: z.string().min(1),
    primaryMediaSourceAssetId: z.string().nullable(),
    primaryMediaDurationSeconds: z.number().multipleOf(1).nonnegative().nullable(),
});
export const PendingRecordingDerivativeSessionsResponseSchema = z.object({
    items: z.array(PendingRecordingDerivativeSessionSchema),
});
export const ProjectRecordingDerivativeSummaryResponseSchema = z.object({
    projectId: UuidSchema,
    kind: RecordingDerivativeKindSchema,
    existingRecordingsCount: z.number().multipleOf(1).nonnegative(),
    missingDerivativeCount: z.number().multipleOf(1).nonnegative(),
});
export const RecordingDerivativeBackfillEventsResponseSchema = z.object({
    count: z.number().multipleOf(1).nonnegative(),
    sessionIds: z.array(UuidSchema),
});
//# sourceMappingURL=Media.js.map